import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Swords,
  X,
  Loader2,
  Trophy,
  Clock,
  Coins,
  UserPlus,
  Play,
  ChevronRight,
  AlertCircle,
  Crown,
  Target,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "fruitCrush_token";

const getAuthHeader = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? `Bearer ${token}` : "";
};

// Tabs
const TABS = [
  { id: "open", label: "Open Challenges", icon: Swords },
  { id: "create", label: "Create", icon: UserPlus },
  { id: "my", label: "My Challenges", icon: Target },
  { id: "history", label: "History", icon: Trophy }
];

function timeLeft(expiresAt) {
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return "Expired";
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return `${hours}h ${mins}m`;
}

export function PvPArenaModal({ isOpen, onClose, userCoins = 0, onCoinsChange, currentLevel = 1 }) {
  const [tab, setTab] = useState("open");
  const [loading, setLoading] = useState(false);
  const [openChallenges, setOpenChallenges] = useState([]);
  const [friendChallenges, setFriendChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState({ active: [], accepted: [], completed: [] });

  // Create form
  const [wager, setWager] = useState(100);
  const [levelId, setLevelId] = useState(currentLevel || 1);
  const [targetUsername, setTargetUsername] = useState("");
  const [isFriendChallenge, setIsFriendChallenge] = useState(false);
  const [creating, setCreating] = useState(false);
  const [accepting, setAccepting] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchChallenges();
      fetchMyChallenges();
    }
  }, [isOpen]);

  const fetchChallenges = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pvp/challenges`, {
        headers: { Authorization: getAuthHeader() }
      });
      const data = await res.json();
      setOpenChallenges(data.open_challenges || []);
      setFriendChallenges(data.friend_challenges || []);
    } catch {
      toast.error("Failed to load challenges");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyChallenges = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pvp/my-challenges`, {
        headers: { Authorization: getAuthHeader() }
      });
      const data = await res.json();
      setMyChallenges(data);
    } catch {
      // silent
    }
  }, []);

  const handleCreate = async () => {
    if (wager < 1) return toast.error("Wager must be at least 1 coin");
    if (wager > userCoins) return toast.error(`Not enough coins. You have ${userCoins}`);
    if (isFriendChallenge && !targetUsername.trim()) return toast.error("Enter opponent's username");

    setCreating(true);
    try {
      const body = { level_id: levelId, wager };
      if (isFriendChallenge && targetUsername.trim()) {
        body.target_username = targetUsername.trim();
      }
      const res = await fetch(`${API_URL}/api/pvp/create-challenge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: getAuthHeader() },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create challenge");

      toast.success(data.message);
      onCoinsChange?.(data.coins_remaining);

      // Navigate to play the level with the PvP seed
      onClose();
      window.dispatchEvent(new CustomEvent("pvp-play", {
        detail: {
          challengeId: data.challenge_id,
          boardSeed: data.board_seed,
          levelId: data.level_id,
          wager: data.wager,
          role: "creator"
        }
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleAccept = async (challenge) => {
    setAccepting(challenge.challenge_id);
    try {
      const res = await fetch(`${API_URL}/api/pvp/accept-challenge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: getAuthHeader() },
        body: JSON.stringify({ challenge_id: challenge.challenge_id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to accept");

      toast.success(data.message);
      onCoinsChange?.(data.coins_remaining);

      onClose();
      window.dispatchEvent(new CustomEvent("pvp-play", {
        detail: {
          challengeId: data.challenge_id,
          boardSeed: data.board_seed,
          levelId: data.level_id,
          wager: data.wager,
          role: "opponent"
        }
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAccepting(null);
    }
  };

  const handlePlayCreatorChallenge = (challenge) => {
    onClose();
    window.dispatchEvent(new CustomEvent("pvp-play", {
      detail: {
        challengeId: challenge.challenge_id,
        boardSeed: challenge.board_seed,
        levelId: challenge.level_id,
        wager: challenge.wager,
        role: "creator"
      }
    }));
  };

  const handleCancel = async (challengeId) => {
    try {
      const res = await fetch(`${API_URL}/api/pvp/cancel-challenge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: getAuthHeader() },
        body: JSON.stringify({ challenge_id: challengeId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to cancel");
      toast.success(data.message);
      fetchMyChallenges();
      fetchChallenges();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-3 bg-black/70 backdrop-blur-sm" data-testid="pvp-arena-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Swords className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">PvP Arena</h2>
              <p className="text-white/70 text-xs">Challenge players for coins</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
              <span className="text-sm">🪙</span>
              <span className="text-white font-bold text-sm" data-testid="pvp-coin-balance">{userCoins.toLocaleString()}</span>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/20 transition-colors" data-testid="pvp-close">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 bg-slate-800/50">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); if (t.id === "open") fetchChallenges(); if (t.id === "my" || t.id === "history") fetchMyChallenges(); }}
                data-testid={`pvp-tab-${t.id}`}
                className={cn(
                  "flex-1 flex flex-col items-center gap-1 py-2.5 text-xs font-medium transition-colors",
                  tab === t.id ? "text-orange-400 border-b-2 border-orange-400" : "text-slate-400 hover:text-white"
                )}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">

          {/* Open Challenges Tab */}
          {tab === "open" && (
            <>
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>
              ) : (
                <>
                  {friendChallenges.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-amber-400 font-semibold text-sm flex items-center gap-2"><UserPlus className="w-4 h-4" /> Challenges for You</p>
                      {friendChallenges.map((ch) => (
                        <ChallengeCard key={ch.challenge_id} challenge={ch} onAccept={handleAccept} accepting={accepting} />
                      ))}
                    </div>
                  )}
                  {openChallenges.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-slate-400 font-semibold text-sm">Open Challenges</p>
                      {openChallenges.map((ch) => (
                        <ChallengeCard key={ch.challenge_id} challenge={ch} onAccept={handleAccept} accepting={accepting} />
                      ))}
                    </div>
                  ) : (
                    !friendChallenges.length && (
                      <div className="text-center py-8 text-slate-500">
                        <Swords className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="font-medium">No open challenges</p>
                        <p className="text-xs mt-1">Create one or check back later</p>
                      </div>
                    )
                  )}
                  <button onClick={fetchChallenges} className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white py-2 transition-colors">
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </button>
                </>
              )}
            </>
          )}

          {/* Create Tab */}
          {tab === "create" && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 font-medium mb-1 block">Level</label>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={levelId}
                  onChange={(e) => setLevelId(parseInt(e.target.value) || 1)}
                  className="bg-slate-800 border-slate-600 text-white"
                  data-testid="pvp-level-input"
                />
                <p className="text-xs text-slate-500 mt-1">Both players play the same board on this level</p>
              </div>

              <div>
                <label className="text-sm text-slate-300 font-medium mb-1 block">Coin Wager</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🪙</span>
                  <Input
                    type="number"
                    min={1}
                    value={wager}
                    onChange={(e) => setWager(parseInt(e.target.value) || 0)}
                    className="bg-slate-800 border-slate-600 text-white pl-8"
                    data-testid="pvp-wager-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Winner takes all ({(wager * 2).toLocaleString()} total pot)</p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsFriendChallenge(false)}
                  className={cn(
                    "flex-1 py-2 rounded-lg text-sm font-medium transition-colors border",
                    !isFriendChallenge ? "bg-orange-500/20 border-orange-400 text-orange-300" : "border-slate-600 text-slate-400 hover:text-white"
                  )}
                  data-testid="pvp-open-toggle"
                >
                  Open Challenge
                </button>
                <button
                  onClick={() => setIsFriendChallenge(true)}
                  className={cn(
                    "flex-1 py-2 rounded-lg text-sm font-medium transition-colors border",
                    isFriendChallenge ? "bg-orange-500/20 border-orange-400 text-orange-300" : "border-slate-600 text-slate-400 hover:text-white"
                  )}
                  data-testid="pvp-friend-toggle"
                >
                  Friend Challenge
                </button>
              </div>

              {isFriendChallenge && (
                <div>
                  <label className="text-sm text-slate-300 font-medium mb-1 block">Opponent Username</label>
                  <Input
                    value={targetUsername}
                    onChange={(e) => setTargetUsername(e.target.value)}
                    placeholder="Enter player name..."
                    className="bg-slate-800 border-slate-600 text-white"
                    data-testid="pvp-target-input"
                  />
                </div>
              )}

              <div className="bg-slate-800/50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Your wager</span>
                  <span className="text-yellow-400 font-bold">🪙 {wager.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Opponent matches</span>
                  <span className="text-yellow-400 font-bold">🪙 {wager.toLocaleString()}</span>
                </div>
                <div className="border-t border-slate-600 my-1" />
                <div className="flex justify-between text-white font-bold">
                  <span>Winner takes</span>
                  <span className="text-green-400">🪙 {(wager * 2).toLocaleString()}</span>
                </div>
              </div>

              <Button
                onClick={handleCreate}
                disabled={creating || wager > userCoins}
                className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold"
                data-testid="pvp-create-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Swords className="w-4 h-4 mr-2" />}
                {creating ? "Creating..." : "Create Challenge & Play"}
              </Button>

              {wager > userCoins && (
                <p className="text-red-400 text-xs text-center flex items-center justify-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Not enough coins
                </p>
              )}
            </div>
          )}

          {/* My Challenges Tab */}
          {tab === "my" && (
            <>
              {/* Challenges waiting for me to play (creator) */}
              {myChallenges.active?.filter(c => c.status === "waiting_for_creator").map((ch) => (
                <div key={ch.challenge_id} className="bg-slate-800 rounded-xl p-3 border border-yellow-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-yellow-400 text-xs font-bold">PLAY NOW</span>
                    <span className="text-slate-400 text-xs"><Clock className="w-3 h-3 inline mr-1" />{timeLeft(ch.expires_at)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white text-sm font-medium">Level {ch.level_id}</p>
                      <p className="text-yellow-400 text-xs">🪙 {ch.wager} wager</p>
                    </div>
                    <Button size="sm" onClick={() => handlePlayCreatorChallenge(ch)} className="bg-yellow-600 hover:bg-yellow-700" data-testid={`pvp-play-${ch.challenge_id}`}>
                      <Play className="w-4 h-4 mr-1" /> Play
                    </Button>
                  </div>
                </div>
              ))}

              {/* Open challenges (waiting for opponent) */}
              {myChallenges.active?.filter(c => c.status === "open").map((ch) => (
                <div key={ch.challenge_id} className="bg-slate-800 rounded-xl p-3 border border-slate-600">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-green-400 text-xs font-bold">WAITING FOR OPPONENT</span>
                    <span className="text-slate-400 text-xs"><Clock className="w-3 h-3 inline mr-1" />{timeLeft(ch.expires_at)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white text-sm font-medium">Level {ch.level_id} {ch.target_username ? `vs ${ch.target_username}` : "(Open)"}</p>
                      <p className="text-yellow-400 text-xs">🪙 {ch.wager} wager | Your score: {ch.creator_score}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleCancel(ch.challenge_id)} className="border-red-500/50 text-red-400 hover:bg-red-500/20" data-testid={`pvp-cancel-${ch.challenge_id}`}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ))}

              {/* Challenges I accepted (need to play as opponent) */}
              {myChallenges.accepted?.map((ch) => (
                <div key={ch.challenge_id} className="bg-slate-800 rounded-xl p-3 border border-orange-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-orange-400 text-xs font-bold">YOUR TURN TO PLAY</span>
                    <span className="text-slate-400 text-xs"><Clock className="w-3 h-3 inline mr-1" />{timeLeft(ch.expires_at)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white text-sm font-medium">Level {ch.level_id} vs {ch.creator_name}</p>
                      <p className="text-yellow-400 text-xs">🪙 {ch.wager * 2} pot</p>
                    </div>
                    <Button size="sm" onClick={() => { onClose(); window.dispatchEvent(new CustomEvent("pvp-play", { detail: { challengeId: ch.challenge_id, boardSeed: ch.board_seed, levelId: ch.level_id, wager: ch.wager, role: "opponent" } })); }} className="bg-orange-600 hover:bg-orange-700">
                      <Play className="w-4 h-4 mr-1" /> Play
                    </Button>
                  </div>
                </div>
              ))}

              {(!myChallenges.active?.length && !myChallenges.accepted?.length) && (
                <div className="text-center py-8 text-slate-500">
                  <Target className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="font-medium">No active challenges</p>
                  <p className="text-xs mt-1">Create a new challenge to get started</p>
                </div>
              )}
            </>
          )}

          {/* History Tab */}
          {tab === "history" && (
            <>
              {myChallenges.completed?.length > 0 ? (
                myChallenges.completed.map((ch) => (
                  <ResultCard key={ch.challenge_id} challenge={ch} />
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Trophy className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="font-medium">No completed matches</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ChallengeCard({ challenge, onAccept, accepting }) {
  return (
    <div className="bg-slate-800 rounded-xl p-3 border border-slate-700 hover:border-orange-500/40 transition-colors" data-testid={`challenge-card-${challenge.challenge_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
            {(challenge.creator_name || "?")[0].toUpperCase()}
          </div>
          <div>
            <p className="text-white text-sm font-medium">{challenge.creator_name}</p>
            <p className="text-slate-400 text-xs">Level {challenge.level_id}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-yellow-400 font-bold text-sm">🪙 {challenge.wager}</p>
          <p className="text-slate-500 text-xs"><Clock className="w-3 h-3 inline mr-1" />{timeLeft(challenge.expires_at)}</p>
        </div>
      </div>
      <div className="mt-2 flex justify-between items-center">
        <span className="text-green-400 text-xs">Pot: 🪙 {(challenge.wager * 2).toLocaleString()}</span>
        <Button
          size="sm"
          onClick={() => onAccept(challenge)}
          disabled={accepting === challenge.challenge_id}
          className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white text-xs"
          data-testid={`pvp-accept-${challenge.challenge_id}`}
        >
          {accepting === challenge.challenge_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Swords className="w-3 h-3 mr-1" /> Accept</>}
        </Button>
      </div>
    </div>
  );
}

function ResultCard({ challenge }) {
  const isWinner = challenge.winner_id !== null;
  const isDraw = !challenge.winner_id;

  return (
    <div className={cn(
      "bg-slate-800 rounded-xl p-3 border",
      isDraw ? "border-slate-500/30" : "border-slate-700"
    )} data-testid={`pvp-result-${challenge.challenge_id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-slate-400 text-xs">Level {challenge.level_id}</span>
        {isDraw ? (
          <span className="text-slate-400 text-xs font-bold">DRAW</span>
        ) : (
          <span className="text-amber-400 text-xs font-bold flex items-center gap-1"><Crown className="w-3 h-3" /> {challenge.winner_name} wins</span>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className={cn("text-sm font-medium", challenge.winner_id === challenge.creator_id ? "text-green-400" : "text-white")}>
            {challenge.creator_name}: {challenge.creator_score?.toLocaleString() || 0}
            <span className="text-slate-500 text-xs ml-1">({challenge.creator_moves_left || 0} moves left)</span>
          </p>
          <p className={cn("text-sm font-medium", challenge.winner_id === challenge.opponent_id ? "text-green-400" : "text-white")}>
            {challenge.opponent_name}: {challenge.opponent_score?.toLocaleString() || 0}
            <span className="text-slate-500 text-xs ml-1">({challenge.opponent_moves_left || 0} moves left)</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-yellow-400 font-bold text-sm">🪙 {(challenge.wager * 2).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

export default PvPArenaModal;
